import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Fade,
  Backdrop,
  alpha,
  Paper,
  IconButton,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { SERVICE_TYPES, FREQUENCY_TYPES, STATUS_LABELS, SERVICE_STATUS } from "../../constants";
import { formatDateInput } from "../../utils";
import { validateClosingHour } from "../../../../utils/formatters";
import { sortTechnicians } from "../../../../utils/sortUtils";
import TimeInput24H from "../../../../components/TimeInput24H";

/**
 * Modal mejorado para editar un servicio
 */
const EditServiceModal = ({
  open,
  onClose,
  selected,
  tecnicos,
  onSubmit,
}) => {
  const [form, setForm] = useState({});
  const [validationError, setValidationError] = useState(null);

  const LOCKED_TIME_TYPES = [
    { value: "MIP", label: "Servicio MIP" },
    { value: "NEBULIZACION", label: "Nebulizacion" },
    { value: "MICROINYECCION", label: "Microinyección" },
    { value: "MOSCA", label: "Mosca" },
    { value: "ABEJA", label: "Abeja" },
  ];

  const closingHour = selected?.branch?.closingHour || null;

  // Ordenar técnicos alfabéticamente
  const sortedTecnicos = useMemo(() => sortTechnicians(tecnicos || []), [tecnicos]);

  useEffect(() => {
    if (!selected) return;
  
    setForm({
      technicians:
        selected.technicians?.map((t) =>
          typeof t === "object" ? t._id : t
        ) || [],
      date: formatDateInput(selected.date),
      hour: selected.hour || "",
      type: selected.type || "",
      status: selected.status || SERVICE_STATUS.PENDING,
      notes: selected.notes || "",
      frequency: selected.frequency || "",
    });
  
    setValidationError(null);
  }, [selected]);

  const timeValidation = useMemo(() => {
    // Solo validar si hay fecha, hora y sucursal seleccionada
    if (!form.date || !form.hour || !form.type ) {
      return { isValid: true, errorMessage: null };
    }
    
    // Solo validar hora de cierre si es servicio protegido 
    const isProtectedTimeType = LOCKED_TIME_TYPES.find((t) => t.value === form.type);
    if (isProtectedTimeType) {  
      return validateClosingHour(form.date, form.hour, closingHour);
    }
    return { isValid: true, errorMessage: null };
  }, [form.date, form.hour, form.type, closingHour]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value || "" }));
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleSubmit = () => {
    // Validar antes de enviar
    if (!timeValidation.isValid) {
      setValidationError(timeValidation.errorMessage || "La hora del servicio no es válida");
      return; // No enviar si la validación falla
    }

    setValidationError(null);

    // Incluir client y branch del servicio original para evitar que se borren
    const clientId = selected?.client?._id || selected?.client || undefined;
    const branchId = selected?.branch?._id || selected?.branch || undefined;

    const normalized = {
      technicians: form.technicians || [],
      date: form.date || undefined,
      hour: form.hour || undefined,
      type: form.type || undefined,
      status: form.status || SERVICE_STATUS.PENDING,
      notes: form.notes || "",
      frequency: form.frequency || undefined,
      client: clientId,
      branch: branchId,
    };

    // Eliminar campos vacíos excepto technician, hour, client y branch
    Object.keys(normalized).forEach((key) => {
      if (
        key !== "technicians" &&
        key !== "hour" &&
        key !== "client" &&
        key !== "branch" &&
        (normalized[key] === "" || normalized[key] === undefined)
      ) {
        delete normalized[key];
      }
    });

    onSubmit(normalized);
  };

  const handleClose = () => {
    setForm({});
    setValidationError(null);
    onClose();
  };

  if (!selected) return null;

  const serviceTypeLabel =
    SERVICE_TYPES.find((t) => t.value === selected.type)?.label || selected.type;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      BackdropProps={{
        sx: {
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(2px)",
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 520 },
            maxHeight: "85vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: "#fff",
                p: 2,
                borderBottom: `1px solid ${alpha("#000", 0.08)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                  sx={{
                    p: 0.75,
                    borderRadius: 1,
                    background: alpha("#1976d2", 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EditIcon sx={{ fontSize: 20, color: "#1976d2" }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "text.primary" }}>
                  Editar servicio: {serviceTypeLabel}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    background: alpha("#000", 0.05),
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Form Content */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                p: 2.5,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: alpha("#1976d2", 0.05),
                },
                "&::-webkit-scrollbar-thumb": {
                  background: alpha("#1976d2", 0.2),
                  borderRadius: 1,
                  "&:hover": {
                    background: alpha("#1976d2", 0.3),
                  },
                },
              }}
            >
              <Stack spacing={2}>
                <TextField
                  select
                  size="small"
                  label="Tipo de servicio"
                  value={form.type}
                  onChange={(e) => handleChange("type", e.target.value)}
                  fullWidth
                  required
                >
                  <MenuItem value="">-- Seleccionar --</MenuItem>
                  {SERVICE_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  type="date"
                  size="small"
                  label="Fecha"
                  value={form.date}
                  onChange={(e) => handleChange("date", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                />

                <TimeInput24H
                  size="small"
                  label="Hora (24H)"
                  value={form.hour}
                  onChange={(e) => handleChange("hour", e.target.value)}
                  fullWidth
                  error={!timeValidation.isValid}
                  helperText={!timeValidation.isValid ? timeValidation.errorMessage : null}
                />

                <TextField
                  select
                  size="small"
                  label="Recurrencia"
                  value={form.frequency}
                  onChange={(e) => handleChange("frequency", e.target.value)}
                  fullWidth
                >
                  <MenuItem value="">-- Seleccionar --</MenuItem>
                  {FREQUENCY_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  SelectProps={{
                    multiple: true,
                  }}
                  size="small"
                  label="Técnicos"
                  value={form.technicians || []}
                  onChange={(e) => handleChange("technicians", e.target.value)}
                  fullWidth
                >
                  {sortedTecnicos.map((t) => (
                    <MenuItem key={t._id} value={t._id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Estado"
                  value={form.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  fullWidth
                >
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  size="small"
                  label="Notas"
                  value={form.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                />
              </Stack>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${alpha("#000", 0.08)}`,
                background: "#fff",
                flexShrink: 0,
                display: "flex",
                gap: 1.5,
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={handleClose} size="small" sx={{ fontWeight: 600, minWidth: 80 }}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                size="small"
                disabled={!timeValidation.isValid}
                sx={{
                  background: "#1976d2",
                  fontWeight: 700,
                  minWidth: 100,
                  "&:hover": {
                    background: "#1565c0",
                  },
                }}
              >
                Guardar
              </Button>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Modal>
  );
};

export default EditServiceModal;
